<template>
    <div class="vmt-container">
        <div class="vmt-header">
            <h2 class="vmt-title">Vendors</h2>
            <button class="vmt-btn vmt-btn-refresh" @click="listVendors()" :disabled="loadingVendors">
                {{ loadingVendors ? 'Loading…' : 'Refresh' }}
            </button>
        </div>

        <!-- Toolbar -->
        <div class="vmt-toolbar">
            <input
                v-model.trim="searchQuery"
                type="search"
                class="vmt-search"
                placeholder="Search vendors..."
                aria-label="Search vendors"
            />
            <select v-model="filterStatus" class="vmt-filter">
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
            </select>
        </div>

        <!-- Table -->
        <div class="vmt-table-wrapper">
            <table class="vmt-table">
                <thead>
                    <tr>
                        <th @click="setSort('vendor')" class="vmt-th-sortable">
                            Vendor {{ sortIndicator('vendor') }}
                        </th>
                        <th @click="setSort('vendorName')" class="vmt-th-sortable">
                            Name {{ sortIndicator('vendorName') }}
                        </th>
                        <th @click="setSort('status')" class="vmt-th-sortable">
                            Status {{ sortIndicator('status') }}
                        </th>
                        <th @click="setSort('phoneNumber')" class="vmt-th-sortable">
                            Phone {{ sortIndicator('phoneNumber') }}
                        </th>
                        <th @click="setSort('email')" class="vmt-th-sortable">
                            Email {{ sortIndicator('email') }}
                        </th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="vendor in paginatedVendors" :key="vendor.vendor" :class="{ 'vmt-row-selected': vendor.vendor === selectedVendor }">
                        <td class="vmt-td-code">{{ vendor.vendor }}</td>
                        <td>{{ vendor.vendorName || '—' }}</td>
                        <td>{{ vendor.status || '—' }}</td>
                        <td>{{ vendor.phoneNumber || '—' }}</td>
                        <td>{{ vendor.email || '—' }}</td>
                        <td>
                            <button class="vmt-btn vmt-btn-edit" @click="selectVendor(vendor.vendor)">Edit</button>
                        </td>
                    </tr>
                    <tr v-if="!paginatedVendors.length">
                        <td colspan="6" class="vmt-empty">
                            {{ loadingVendors ? 'Loading vendors…' : 'No vendors match your filters.' }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div class="vmt-pagination">
            <span class="vmt-pagination-info">
                {{ paginationStart }}–{{ paginationEnd }} of {{ filteredVendors.length }}
            </span>
            <button class="vmt-btn vmt-btn-page" :disabled="currentPage === 1" @click="currentPage--">‹ Prev</button>
            <button class="vmt-btn vmt-btn-page" :disabled="currentPage >= totalPages" @click="currentPage++">Next ›</button>
            <select v-model.number="pageSize" class="vmt-page-size">
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
                <option :value="999999">All</option>
            </select>
        </div>
    </div>
    <AlertUser v-if="toggleAlertBox" @closeAlertBox="closeAlertBox($event)" :message="alertMessage"/>
</template>

<script>
import { API } from "aws-amplify";
import AlertUser from "../../../components/Popups/AlertUser.vue";

export default {
    name: "VendorManagementTable",

    components: {
        AlertUser,
    },

    props: {
        // Bump this key from the parent to force a reload.
        refreshKey: { type: Number, default: 0 },
    },

    emits: ["selectVendor"],

    data() {
        return {
            vendorData: [],
            loadingVendors: false,
            selectedVendor: null,

            searchQuery: "",
            filterStatus: "",

            sortField: "vendor",
            sortDirection: "asc",

            currentPage: 1,
            pageSize: 50,

            toggleAlertBox: false,
            alertMessage: "",
        };
    },

    computed: {
        filteredVendors() {
            let list = this.vendorData;

            if (this.searchQuery) {
                const q = this.searchQuery.toLowerCase();
                list = list.filter((v) =>
                    (v.vendor || "").toLowerCase().includes(q) ||
                    (v.vendorName || "").toLowerCase().includes(q) ||
                    (v.email || "").toLowerCase().includes(q) ||
                    (v.phoneNumber || "").toLowerCase().includes(q)
                );
            }

            if (this.filterStatus) {
                list = list.filter((v) => v.status === this.filterStatus);
            }

            const field = this.sortField;
            const dir = this.sortDirection === "asc" ? 1 : -1;

            list = [...list].sort((a, b) => {
                let valA = a[field];
                let valB = b[field];
                if (valA == null) valA = "";
                if (valB == null) valB = "";
                if (typeof valA === "number" && typeof valB === "number") return (valA - valB) * dir;
                return String(valA).localeCompare(String(valB)) * dir;
            });

            return list;
        },

        totalPages() {
            return Math.max(1, Math.ceil(this.filteredVendors.length / this.pageSize));
        },

        paginatedVendors() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.filteredVendors.slice(start, start + this.pageSize);
        },

        paginationStart() {
            return this.filteredVendors.length ? (this.currentPage - 1) * this.pageSize + 1 : 0;
        },

        paginationEnd() {
            return Math.min(this.currentPage * this.pageSize, this.filteredVendors.length);
        },
    },

    watch: {
        searchQuery() { this.currentPage = 1; },
        filterStatus() { this.currentPage = 1; },
        refreshKey() { this.listVendors(); },
    },

    mounted() {
        this.listVendors();
    },

    methods: {
        setSort(field) {
            if (this.sortField === field) {
                this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
            } else {
                this.sortField = field;
                this.sortDirection = "asc";
            }
        },

        sortIndicator(field) {
            if (this.sortField !== field) return "";
            return this.sortDirection === "asc" ? "▲" : "▼";
        },

        selectVendor(vendor) {
            this.selectedVendor = vendor;
            this.$emit("selectVendor", vendor);
        },

        closeAlertBox(toggleAlertBox) {
            this.toggleAlertBox = toggleAlertBox;
        },

        // Inline query that omits createdAt/updatedAt to avoid AWSDateTime null issues.
        async listVendors() {
            this.loadingVendors = true;
            let nextToken = null;
            let vendors = [];
            try {
                do {
                    const response = await API.graphql({
                        query: `query listVendors($nextToken: String) {
                            listVendors(limit: 1000, nextToken: $nextToken) {
                                items {
                                    vendor
                                    vendorName
                                    status
                                    phoneNumber
                                    email
                                    billFrequency
                                    billRunDate
                                    invoiceSequence
                                }
                                nextToken
                            }
                        }`,
                        variables: { nextToken },
                        authMode: "AMAZON_COGNITO_USER_POOLS",
                    });
                    const data = response.data.listVendors;
                    vendors = vendors.concat(data.items || []);
                    nextToken = data.nextToken;
                } while (nextToken);

                this.vendorData = vendors;
            } catch (e) {
                console.log(e);
                this.toggleAlertBox = true;
                this.alertMessage = "Error loading vendors";
            } finally {
                this.loadingVendors = false;
            }
        },
    },
};
</script>

<style scoped>
.vmt-container {
    width: 100%;
}

.vmt-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}

.vmt-title {
    margin: 0;
}

.vmt-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 10px;
}

.vmt-search,
.vmt-filter,
.vmt-page-size {
    padding: 8px;
    font-size: 14px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    outline: none;
}

.vmt-search {
    flex: 1;
    min-width: 180px;
}

.vmt-table-wrapper {
    width: 100%;
    overflow-x: auto;
}

.vmt-table {
    width: 100%;
    border-collapse: collapse;
}

.vmt-table th,
.vmt-table td {
    padding: 8px 10px;
    text-align: left;
    border-bottom: 1px solid #eee;
    font-size: 14px;
}

.vmt-th-sortable {
    cursor: pointer;
    user-select: none;
}

.vmt-td-code {
    font-weight: bold;
}

.vmt-row-selected {
    background-color: #e6f7ff;
}

.vmt-empty {
    text-align: center;
    color: #888;
    padding: 20px;
}

.vmt-btn {
    cursor: pointer;
    border: none;
    border-radius: 10px;
    padding: 6px 12px;
    color: #fff;
    background-color: #32ccfe;
    transition: background-color 0.3s;
}

.vmt-btn:hover {
    background-color: #2baeda;
}

.vmt-btn:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
}

.vmt-btn-edit {
    background-color: #33f18a;
    color: #033;
}

.vmt-btn-edit:hover {
    background-color: #28d576;
}

.vmt-pagination {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
    flex-wrap: wrap;
}

.vmt-pagination-info {
    font-size: 13px;
    color: #666;
}
</style>
